import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next"
import {
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Breadcrumbs,
  Link,
  Alert,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import EntityForm from "../../components/EntityForm";
import api from "../../services/api";
import { renderFormField } from "../../components/FormFields";
import { FormField, Stall, Stable } from "../../interfaces";
import { useCryptoNavigation, useAppNavigation } from "../../App";

export default function StallForm() {
  const { t } = useTranslation()
  const { navigateToPage } = useCryptoNavigation();
  const { getParam } = useAppNavigation();

  const id = getParam("id");
  const mode = getParam("mode");
  const tenantIdParam = getParam("tenantId");
  const stableIdParam = getParam("stableId");
  const stableNameParam = getParam("stableName");
  const isEditMode = mode === "edit" && Boolean(id);
  
  // Pegar o tenantId do localStorage como fallback se não vier nos parâmetros
  const effectiveTenantId = tenantIdParam || localStorage.getItem('selectedTenantId');
  
  // Log para debug
  console.log("StallForm params:", {
    id,
    mode,
    tenantIdParam,
    effectiveTenantId,
    stableIdParam,
    stableNameParam,
    isEditMode
  });

  const [stables, setStables] = useState<Stable[]>([]);
  const [horses, setHorses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStableId, setSelectedStableId] = useState<number | null>(
    stableIdParam ? Number(stableIdParam) : null
  );
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [currentHorseId, setCurrentHorseId] = useState<number | null>(null);
  const [hasAssociatedHorse, setHasAssociatedHorse] = useState(false);

  // =============================
  // 🔸 Carregar estábulos
  // =============================
  useEffect(() => {
    const fetchStables = async () => {
      if (!effectiveTenantId) {
        console.warn("effectiveTenantId não está definido");
        return;
      }
      
      try {
        console.log("Carregando estábulos para tenantId:", effectiveTenantId);
        const response = await api.get(`/stable/${effectiveTenantId}`);
        let stablesData: Stable[] = [];
        if (Array.isArray(response.data)) stablesData = response.data;
        else if (response.data?.stables) stablesData = response.data.stables;
        setStables(stablesData);
      } catch (error) {
        console.error("Erro ao carregar estábulos:", error);
        setError(t("stalls.errorLoadStables"));
      } finally {
        setLoading(false);
      }
    };
    fetchStables();
  }, [effectiveTenantId]);

  // =============================
  // 🔸 Carregar cavalos disponíveis
  // =============================
  useEffect(() => {
    const fetchAvailableHorses = async () => {
      if (!effectiveTenantId) {
        console.warn("effectiveTenantId não está definido para carregar cavalos");
        return;
      }
      
      try {
        console.log("🐴 Carregando cavalos para tenantId:", effectiveTenantId);
        const response = await api.get(`/horse/${effectiveTenantId}`);
        console.log("📦 Resposta da API de cavalos:", response.data);
        
        // Tratar diferentes formatos de resposta
        let allHorses: any[] = [];
        if (Array.isArray(response.data)) {
          allHorses = response.data;
        } else if (response.data?.horses && Array.isArray(response.data.horses)) {
          allHorses = response.data.horses;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          allHorses = response.data.data;
        }
        
        console.log("📊 Total de cavalos encontrados:", allHorses.length);
        
        // Em modo de edição, incluir o cavalo atual se houver
        let availableHorses: any[];
        if (isEditMode && currentHorseId) {
          availableHorses = allHorses.filter(
            (horse: any) => 
              (!horse.stallId || horse.stallId === null || horse.stallId === 0) || 
              horse.horseId === currentHorseId
          );
          console.log(`✅ Modo edição: cavalos disponíveis incluindo atual (${currentHorseId}):`, availableHorses.length);
        } else {
          // Filtrar apenas cavalos sem baia
          availableHorses = allHorses.filter(
            (horse: any) => !horse.stallId || horse.stallId === null || horse.stallId === 0
          );
          console.log("✅ Modo criação: cavalos sem baia:", availableHorses.length);
        }
        
        setHorses(availableHorses);
      } catch (error) {
        console.error("❌ Erro ao carregar cavalos disponíveis:", error);
        // Não mostrar erro aqui pois pode não haver cavalos cadastrados
      }
    };

    if (effectiveTenantId) {
      fetchAvailableHorses();
    }
  }, [effectiveTenantId, isEditMode, currentHorseId]);

  // =============================
  // 🔸 Verificar se a baia tem cavalo associado
  // =============================
  useEffect(() => {
    const checkAssociatedHorse = async () => {
      if (isEditMode && id && effectiveTenantId) {
        try {
          console.log("Verificando cavalo associado - tenantId:", effectiveTenantId, "id:", id);
          const response = await api.get(`/stall/${effectiveTenantId}/${id}`);
          const stallData = response.data?.stall || response.data;
          if (stallData?.horseId) {
            setHasAssociatedHorse(true);
            setCurrentHorseId(stallData.horseId);
          }
        } catch (error) {
          console.error("Erro ao verificar cavalo associado:", error);
        }
      }
    };
    checkAssociatedHorse();
  }, [isEditMode, id, effectiveTenantId]);

  // =============================
  // 🔸 Campos do formulário
  // =============================
  const formFields: FormField[] = [
    {
      name: "stallName",
      label: t("stalls.nameLabel"),
      type: "text",
      required: true,
      fullWidth: true,
      helperText: t("stalls.nameHelper"),
    },
    {
      name: "stableId",
      label: t("fields.stable"),
      type: "select",
      required: true,
      options: stables.map((stable) => ({
        value: stable.stableId,
        label: stable.stableName,
      })),
      fullWidth: true,
      helperText: t("stalls.stableHelper"),
      disabled: !!stableIdParam,
    },
    {
      name: "horseId",
      label: t("fields.horse"),
      type: "select",
      required: false,
      options: [
        { value: "", label: t("stalls.noHorse") },
        ...horses.map((horse) => ({
          value: horse.horseId,
          label: horse.horseName,
        })),
      ],
      fullWidth: true,
      helperText: t("stalls.horseHelper"),
    },
  ];

  // =============================
  // 🔸 Transformações de dados
  // =============================
  const transformBeforeSave = (data: any): any => {
    console.log("🔄 Dados recebidos no transformBeforeSave:", data);
    
    const processed = { ...data };

    // NA EDIÇÃO O EntityForm PASSA SÓ OS CAMPOS ALTERADOS.
    //
    // Tratar campo ausente como "vazio" apagava o vínculo: ao renomear a
    // baia, o cavalo não vinha no diff, o código concluía "nenhum cavalo" e
    // mandava horseId null — desalojando o cavalo que estava lá.
    const presente = (campo: string) =>
      Object.prototype.hasOwnProperty.call(data, campo);
    const mexeuNoCavalo = !isEditMode || presente("horseId");
    const mexeuNoEstabulo = !isEditMode || presente("stableId");

    if (mexeuNoEstabulo) {
      // Converter stableId para número
      if (processed.stableId && typeof processed.stableId === "string") {
        processed.stableId = parseInt(processed.stableId as any, 10);
      }

      // Se não houver stableId, usar o selecionado
      if (selectedStableId && !processed.stableId) {
        processed.stableId = selectedStableId;
      }
    } else {
      delete processed.stableId;
    }

    if (!mexeuNoCavalo) {
      // Intocado: fora do pedido, para o servidor manter o que está gravado.
      delete processed.horseId;
      delete processed.horseName;
    } else if (!processed.horseId || processed.horseId === "" || processed.horseId === "0") {
      // Sem cavalo selecionado
      processed.horseId = null;
      processed.horseName = null;
      console.log("❌ Nenhum cavalo selecionado");
    } else {
      // Converter para número se necessário
      const horseIdNumber = typeof processed.horseId === "string" 
        ? parseInt(processed.horseId, 10) 
        : processed.horseId;
      
      processed.horseId = horseIdNumber;
      
      // Buscar o nome do cavalo
      const selectedHorse = horses.find(h => h.horseId === horseIdNumber);
      processed.horseName = selectedHorse ? selectedHorse.horseName : null;
      
      console.log(`✅ Cavalo selecionado: ID=${horseIdNumber}, Nome=${processed.horseName}`);
    }
    
    // stableName acompanha o stableId: sem ele no pedido, mandar o nome
    // sozinho só arriscaria gravar null por cima do que existe.
    if (mexeuNoEstabulo) {
      const selectedStable = stables.find(s => s.stableId === processed.stableId);
      processed.stableName = selectedStable ? selectedStable.stableName : null;
    } else {
      delete processed.stableName;
    }
    
    // Adicionar stallId se estiver em modo de edição
    if (isEditMode && id) {
      processed.stallId = parseInt(id, 10);
    }
    
    console.log("💾 Dados finais a serem salvos:", processed);
    
    return processed;
  };

  const transformAfterLoad = (data: any): any => {
    const stallData = data.stall || data;
    const transformed = {
      stallId: stallData?.stallId || stallData?.id || 0,
      stallName: stallData?.stallName || "",
      stableId: selectedStableId || stallData?.stableId || Number(stableIdParam) || 0,
      stableName: stallData?.stableName,
      horseId: stallData?.horseId || "",
      horseName: stallData?.horseName || null,
    };
    
    console.log("📥 Dados carregados após transformação:", transformed);
    return transformed;
  };

  // =============================
  // 🔸 Validação do formulário
  // =============================
  const validateForm = (formData: any) => {
    const errors: Record<string, string> = {};
    if (!formData.stallName?.trim()) {
      errors.stallName = t("stalls.nameRequired");
    }
    if (!formData.stableId) {
      errors.stableId = t("stalls.stableRequired");
    }
    return errors;
  };

  // =============================
  // 🔸 Validação antes de excluir
  // =============================
  const handleBeforeDelete = async (): Promise<boolean> => {
    if (hasAssociatedHorse) {
      alert(t("stalls.cannotDeleteHasHorse"));
      return false;
    }
    return true;
  };

  // =============================
  // 🔸 Handlers
  // =============================
  const handleSuccess = (data: any) => {
    console.log("✅ Baia salva com sucesso:", data);
    
    // Se um cavalo foi associado, mostrar mensagem de confirmação
    if (data?.horseId || data?.stall?.horseId) {
      const horseId = data?.horseId || data?.stall?.horseId;
      const horse = horses.find(h => h.horseId === horseId);
      if (horse) {
        console.log(`🎉 Cavalo "${horse.horseName}" associado com sucesso!`);
      }
    }
  };

  const handleError = (error: any) => {
    console.error("❌ Erro ao salvar baia:", error);
    console.error("❌ Detalhes do erro:", error.response?.data || error.message);
    setError(t("stalls.errorSave"));
  };

  const handleBack = () => {
    const params: any = { mode: "list" };
    
    // Sempre incluir tenantId na navegação
    if (effectiveTenantId) {
      params.tenantId = effectiveTenantId;
    }
    
    // Se veio de um estábulo específico, incluir também
    if (stableIdParam) {
      params.stableId = stableIdParam;
    }
    
    navigateToPage("baias", params);
  };

  const getSelectedStableName = (): string => {
    if (!selectedStableId) return "";
    const stable = stables.find((s) => s.stableId === selectedStableId);
    return stable ? stable.stableName : "";
  };

  return (
    <>
      {/* CABEÇALHO */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
        >
          <Box>
            <Breadcrumbs aria-label="breadcrumb">
              <Link
                color="inherit"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigateToPage("proprietarios", { mode: "list" });
                }}
              >
                {t("nav.tenants")}
              </Link>
              <Link
                color="inherit"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigateToPage("estabulos", { 
                    mode: "list", 
                    tenantId: effectiveTenantId 
                  });
                }}
              >
                {t("nav.stables")}
              </Link>
              <Link
                color="inherit"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleBack();
                }}
              >
                {t('nav.stalls')}
              </Link>
              <Typography color="text.primary">
                {isEditMode ? t("stalls.editTitle") : t("stalls.newTitle")}
              </Typography>
            </Breadcrumbs>
            <Typography variant="h5" component="h1" sx={{ mt: 1 }}>
              {isEditMode ? t("stalls.editTitle") : t("stalls.newTitle")}
            </Typography>
          </Box>
          <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={handleBack}>
            {t("common.back")}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {selectedStableId && getSelectedStableName() && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("stalls.willBeAssociated")} <strong>{getSelectedStableName()}</strong>
        </Alert>
      )}

      {/* Aviso sobre exclusão quando há cavalo associado */}
      {isEditMode && hasAssociatedHorse && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t("stalls.hasHorseWarning")}
        </Alert>
      )}

      {/* FORMULÁRIO */}
      {effectiveTenantId ? (
        <EntityForm
          title=""
          endpoint={`/stall/${effectiveTenantId}`}
          fields={formFields}
          listPath="/stall"
          listPageName="baias"
          renderField={renderFormField}
          transformBeforeSave={transformBeforeSave}
          transformAfterLoad={transformAfterLoad}
          validateForm={validateForm}
          onSuccess={handleSuccess}
          onError={handleError}
          idField="stallId"
          submitButtonText={t("stalls.saveButton")}
          useEncryptedNavigation
          defaultTenantId={effectiveTenantId}
          key={`stall-form-${refreshTrigger}`}
          onBeforeDelete={handleBeforeDelete}
        />
      ) : (
        <Alert severity="warning">
          {t("stalls.noOwnerSelected")}
        </Alert>
      )}
    </>
  );
}